import { useState, useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useSignupMutation,
  useGoogleLoginMutation,
} from "../../../redux/slices/api/authApiSlice";
import { setCredentials } from "../../../redux/slices/authSlice";
import "../styles/Login.css";
import Button from "../../shared/Button";

// `asModal` renders just the card; `onClose` fires on success; `onSwitch` swaps to login.
const Signup = ({ asModal = false, onClose, onSwitch }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [registerApi, { isLoading }] = useSignupMutation();
  const [googleLoginApi] = useGoogleLoginMutation();

  const [error, setError] = useState("");

  const handleGoogleResponse = async (response) => {
    try {
      const res = await googleLoginApi({
        token: response.credential,
      }).unwrap();
      dispatch(setCredentials(res));
      if (asModal && onClose) onClose();
    } catch (err) {
      setError("Google signup failed");
    }
  };

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });

      window.google.accounts.id.renderButton(
        document.getElementById("googleSignUpDiv"),
        {
          theme: "outline",
          size: "large",
          width: 260,
        }
      );
    }
  }, []);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;
    return strength;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError("");

    if (name === "password") {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await registerApi(formData).unwrap();
      dispatch(setCredentials(res));
      if (asModal && onClose) onClose();
    } catch (err) {
      setError(err?.data?.message || "Signup failed");
    }
  };

  // Full-page mode only: bounce an already-logged-in user away.
  if (!asModal && user) {
    return <Navigate to="/homepage" replace />;
  }

  const getStrengthLabel = () => {
    const labels = ["", "Weak", "Fair", "Good", "Strong"];
    return labels[passwordStrength];
  };

  const getStrengthColor = () => {
    const colors = ["", "#ff6b6b", "#ffd93d", "#6bcf7f", "#4ecdc4"];
    return colors[passwordStrength];
  };

  const card = (
    <div className="auth-card">
        <div className="auth-header">
          <h2 className="auth-title">Get Started</h2>
          <p className="auth-subtitle">Create your DS Visualizer account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="input-wrapper">
            <label htmlFor="name" className="input-label">Full Name</label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              onFocus={() => setFocusedField("name")}
              onBlur={() => setFocusedField(null)}
              className={focusedField === "name" ? "focused" : ""}
              required
            />
            <div className="input-underline"></div>
          </div>

          <div className="input-wrapper">
            <label htmlFor="email" className="input-label">Email Address</label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              className={focusedField === "email" ? "focused" : ""}
              required
            />
            <div className="input-underline"></div>
          </div>

          <div className="input-wrapper">
            <label htmlFor="password" className="input-label">Password</label>
            <div className="password-field">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                className={focusedField === "password" ? "focused" : ""}
                required
              />
              <Button
                type="button"
                variant=""
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "Hide" : "Show"}
              </Button>
            </div>
            <div className="input-underline"></div>
            {formData.password && (
              <div className="password-strength">
                <div className="strength-bar">
                  <div
                    className="strength-fill"
                    style={{
                      width: `${(passwordStrength / 4) * 100}%`,
                      backgroundColor: getStrengthColor(),
                    }}
                  ></div>
                </div>
                <span className="strength-label" style={{ color: getStrengthColor() }}>
                  {getStrengthLabel()}
                </span>
              </div>
            )}
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠</span> {error}
            </div>
          )}

          <Button type="submit" variant="" disabled={isLoading} className="auth-button">
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Creating account...
              </>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <div className="google-container">
          <div id="googleSignUpDiv"></div>
        </div>

        <p className="auth-footer">
          Already have an account?{" "}
          {asModal ? (
            <button type="button" className="auth-link auth-link-button" onClick={onSwitch}>
              Sign in
            </button>
          ) : (
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          )}
        </p>
      </div>
  );

  if (asModal) {
    return card;
  }

  return (
    <div className="auth-container">
      <div className="gradient-bg"></div>
      {card}
    </div>
  );
};

export default Signup;
